import type { CenterArea } from '../types/center.ts';
import type { GameState } from '../types/game-state.ts';
import { TURN_TIME_LIMIT_MS } from '../types/game-state.ts';
import type { Player } from '../types/player.ts';
import { createTilePool } from './board.ts';
import { laneIsFullyRevealed, setupPlayerLane } from './lane.ts';
import type { RNG } from './random.ts';

export type PlayerSpec = {
  id: string;
  nickname: string;
  isHost?: boolean;
};

/**
 * 새 게임 객체 생성. status='WAITING'.
 * 모든 플레이어 줄을 셋업하고, 더미는 남은 타일.
 */
export function createGame(specs: readonly PlayerSpec[], roomCode: string, rng: RNG): GameState {
  if (specs.length < 1 || specs.length > 4) {
    throw new Error(`player count out of range: ${specs.length} (allowed 1~4)`);
  }

  let deck = createTilePool(rng);
  const players: Player[] = [];
  for (const spec of specs) {
    const setup = setupPlayerLane(deck, rng);
    deck = setup.remainingDeck;
    players.push({
      id: spec.id,
      nickname: spec.nickname,
      lane: setup.lane,
      isHost: spec.isHost ?? false,
      connected: true,
    });
  }

  const center: CenterArea = { deck, openPool: [] };

  return {
    roomCode,
    players,
    center,
    currentPlayerIdx: 0,
    bonusActive: false,
    status: 'WAITING',
    winnerId: null,
    holding: null,
    metrics: { startedAt: 0, finishedAt: null },
    turnDeadline: null,
  };
}

export type StartGameOptions = { now?: number };

export function startGame(state: GameState, options: StartGameOptions = {}): GameState {
  if (state.status !== 'WAITING') {
    throw new Error(`cannot start: status is ${state.status}`);
  }
  if (state.players.length < 2) {
    throw new Error(`need at least 2 players to start, got ${state.players.length}`);
  }
  const now = options.now ?? Date.now();
  return {
    ...state,
    status: 'IN_PROGRESS',
    metrics: { ...state.metrics, startedAt: now },
    turnDeadline: now + TURN_TIME_LIMIT_MS,
  };
}

export function isGameFinished(state: GameState): boolean {
  return state.status === 'FINISHED';
}

export function getWinner(state: GameState): Player | null {
  if (state.winnerId === null) return null;
  return state.players.find((p) => p.id === state.winnerId) ?? null;
}

/**
 * 봇과의 1:1 대결 모드 시작 헬퍼.
 * 사람과 봇 각 1명, 사람이 선공.
 */
export function createBotGame(
  playerNickname: string,
  botNickname: string,
  rng: RNG,
  options: StartGameOptions = {},
): GameState {
  const game = createGame(
    [
      { id: 'me', nickname: playerNickname, isHost: true },
      { id: 'bot', nickname: botNickname, isHost: false },
    ],
    'SOLO',
    rng,
  );
  return startGame(game, options);
}

/**
 * 자기 줄 22장 모두 공개되었는지 확인 (승리 조건).
 */
export function isLaneWinning(state: GameState, playerId: string): boolean {
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return false;
  return laneIsFullyRevealed(player.lane);
}
